<template>
  <div style="clear: both">
    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          {{ $t('Loading') }}
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-card>
      <v-toolbar elevation="8" density="compact">
        <v-toolbar-title>{{ $t('History') }}
          <v-tooltip bottom>
            <template v-slot:activator="{ props }">
              <v-btn icon @click="showHistory = !showHistory" v-bind="props" flat>
                <v-icon>{{ showHistory ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
              </v-btn
              >
            </template>
            <span>{{ showHistory ? $t('Hide') : $t('Show') }}</span>
          </v-tooltip>
        </v-toolbar-title>        
      </v-toolbar>

      <v-data-table
        v-if="showHistory"
        :headers="mocVersionHistoryHeaders"
        :items="mocVersionHistoryItems"
        class="elevation-1"
        disable-sort
        dense
        density="compact"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td class="bold-text">
              <v-tooltip top v-if="item.versionId && canDownloadJson">
                <template v-slot:activator="{ props }">
                  <v-chip
                    v-bind="props"
                    @click="downloadVersionFile(item)"
                    :color="getVersionColor(item)"
                    dark
                    variant="flat"
                    small
                    pill
                    >{{ item.version }}</v-chip
                  >
                </template>
                <span>{{ $t('Download_This_Version') }}</span>
              </v-tooltip>
              <v-chip v-if="!item.versionId" dark small pill variant="flat">{{ item.version }}</v-chip>
              <v-chip v-if="item.versionId && !canDownloadJson" :color="getVersionColor(item)" dark small pill variant="flat">{{
                item.version
              }}</v-chip>
            </td>
            <td>
              <span>
                {{ item.status.name }}
              </span>
              <ArchiveButton v-if="isClosed(item.status)" :mocId="moc.mocId" />
            </td>
            <td>
              <div class="bold-text mt-2">
                <v-tooltip top v-if="item.approvals">
                  <template v-slot:activator="{ props }">
                    <v-chip
                      v-bind="props"
                      @click="showApprovals(item, index)"
                      :color="getVersionColor(item)"
                      dark
                      small
                      pill
                      variant="flat"
                      class="text-white"
                      >{{ $t('Approvals') }}</v-chip
                    >
                  </template>
                  <span>{{ $t('See_This_Version') }}</span>
                </v-tooltip>
                <v-tooltip top v-if="item.approvals && canDownloadJson">
                  <template v-slot:activator="{ props }">
                    <v-icon
                      v-bind="props"
                      class="ml-2"
                      size="x-large"
                      @click="downloadApprovalVersionFile(item.approvals)"
                      :color="getVersionColor(item)"
                      >mdi-cloud-download-outline</v-icon
                    >
                  </template>
                  <span>{{ $t('Download_This_Version') }}</span>
                </v-tooltip>
              </div>
              <div class="bold-text mt-2" v-if="canSeeTasks">
                <v-tooltip top v-if="item.tasks && canDownloadJson">
                  <template v-slot:activator="{ props }">
                    <v-chip
                      v-bind="props"
                      @click="downloadTasksVersionFile(item.tasks)"
                      :color="getVersionColor(item)"
                      dark
                      small
                      pill
                      variant="flat"
                      class="text-white"
                      >{{ $t('Tasks') }}</v-chip
                    >
                  </template>
                  <span>{{ $t('Download_This_Version') }}</span>
                </v-tooltip>
                <v-chip v-if="item.tasks && !canDownloadJson" :color="getVersionColor(item)" dark small pill variant="flat" class="text-white">{{
                  $t('Tasks')
                }}</v-chip>
              </div>
              <div v-if="item.revisionReason" class="bold-text mt-2">
                <v-chip :color="getVersionColor(item)" @click="showBeingRevisedReason(item)" dark small pill variant="flat" class="text-white">{{
                  $t('Reason')
                }}</v-chip>
              </div>
              <div v-if="item.sentBackReason" class="bold-text mt-2">
                <v-chip :color="getVersionColor(item)" @click="showSentBackReason(item)" dark small pill variant="flat" class="text-white">{{
                  $t('Reason')
                }}</v-chip>
              </div>
              <div v-if="item.closeStrandedReason" class="bold-text mt-2">
                <v-chip :color="getVersionColor(item)" @click="showCloseStrandedReason(item)" dark small pill variant="flat" class="text-white">{{
                  $t('Justification')
                }}</v-chip>
              </div>
            </td>
            <td>
              <v-chip
                v-if="getOwnerDecision(item).value"
                :color="setOwnerDecisionColor(getOwnerDecision(item).key)"
                @click="showOwnersDecision(item)"
                dark
                small
                pill variant="flat" class="text-white"
                >{{ getOwnerDecision(item).value }}</v-chip
              >
            </td>
            <td>
              <span v-for="(flag, i) in getFlags(item)" :key="i">
                {{ flag }}<span v-if="i !== (item.flags || []).length - 1">, </span> <br />
              </span>
            </td>
            <td>{{ $filters.DateTimeFilter(item.date) }}</td>
            <td>{{ item.user.displayName }}</td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
    <toaster ref="toaster"></toaster>
    <v-dialog v-model="showApprovalsDialog" persistent max-width="1200">
      <v-card v-if="showApprovalsDialog" class="pa-0">
        <v-toolbar color="blue-lighten-4" height="40" window dense flat class="pa-0">
          <span></span>
          <v-spacer></v-spacer>
          <v-icon @click="hideApprovals()" color="error" class="mr-2">mdi-close-circle-outline</v-icon>
        </v-toolbar>
        <v-container>
          <div>
            <strong>{{ $t('Date') }}</strong
            >: {{ $filters.DateTimeFilter(selectedHistoryItem.date) }}
          </div>
        </v-container>
        <approvals
          :moc="mocInHistory"
          :showHistory="true"
          :showPreLoadedApprovals="true"
          :preLoadedApprovals="approvals"
        ></approvals>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showOwnerDecisionDialog" persistent max-width="1200">
      <v-card v-if="showOwnerDecisionDialog" max-height="700" class="pa-0">
        <v-toolbar color="blue-lighten-4" height="40" window dense flat class="pa-0">
          <span></span>
          <v-spacer></v-spacer>
          <v-icon @click="showOwnerDecisionDialog = false" color="error" class="mr-2">mdi-close-circle-outline</v-icon>
        </v-toolbar>

        <owner-decision
          :ownerDecision="selectedOwnerDecision"
          :showHistory="isClosed(selectedOwnerDecisionStatus)"
        ></owner-decision>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showBeingRevisedReasonDialog" persistent max-width="1200">
      <v-card v-if="showBeingRevisedReasonDialog" max-height="700" class="pa-0">
        <v-toolbar color="blue-lighten-4" height="40" window dense flat class="pa-0">
          <span></span>
          <v-spacer></v-spacer>
          <v-icon @click="showBeingRevisedReasonDialog = false" color="error">mdi-close-circle-outline</v-icon>
        </v-toolbar>

        <being-revised-reason :revisionReason="selectedRevisionReason" :revisionHistory="[]"></being-revised-reason>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showSentBackReasonDialog" persistent max-width="1200">
      <v-card v-if="showSentBackReasonDialog" max-height="700" class="pa-0">
        <v-toolbar color="blue-lighten-4" height="40" window dense flat class="pa-0">
          <span></span>
          <v-spacer></v-spacer>
          <v-icon @click="showSentBackReasonDialog = false" color="error" class="mr-2">mdi-close-circle-outline</v-icon>
        </v-toolbar>

        <sent-back-reason :sentBackReason="selectedSentBackReason" :sentBackHistory="[]"></sent-back-reason>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showCloseStrandedReasonDialog" persistent max-width="600">
      <v-card v-if="showCloseStrandedReasonDialog" class="pa-0">
        <v-toolbar color="error" height="40" window dense flat class="pa-0">
          <span>{{ this.moc.status.name }}</span>
          <v-spacer></v-spacer>
          <v-btn @click="showCloseStrandedReasonDialog = false" icon>
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
          <v-card-text>
            <v-container>
              
             <a :href="selectedCloseStrandedReason.justification">{{ selectedCloseStrandedReason.justification }}</a>
            
            </v-container>
        </v-card-text>
          
        
      </v-card>

    </v-dialog>
  </div>
</template>

<script>
import Toaster from '@/components/Toaster'
import { FormStatus } from '@/common/'
import { Flags } from '@/common/flags'
import MOCService from '@/services/moc.service'
import TaskService from '@/services/task.service'
import ApprovalService from '@/services/approval.service'
import downloadFile from '@/common/downloadFile'
import mocStatusMixins from '@/mixins/mocStatusMixins'
import { mapState } from 'vuex'
import ArchiveButton from '@/components/archive/ArchiveButton'
import Approvals from '@/components/mocForm/Approvals'
import { DecisionTypes } from '../../common/types'
import OwnerDecision from '@/components/mocForm/OwnerDecision'
import moment from 'moment'
import BeingRevisedReason from '@/components/mocForm/BeingRevisedReason'
import SentBackReason from '@/components/mocForm/SentBackReason'

export default {
  name: 'History',
  mixins: [mocStatusMixins],
  data() {
    let mocVersionHistoryHeaders = [
      {
        title: this.$t('Version'),
        key: 'version'
      },
      {
        title: this.$t('Status'),
        key: 'status'
      }
      // {
      //   title: this.$t('Approvals'),
      //   key: 'approvals'
      // }
    ]
    mocVersionHistoryHeaders = mocVersionHistoryHeaders.concat([
      {
        title: this.$t('Details')
      },
      {
        title: this.$t('Owner_Decision')
      },
      {
        title: this.$t('Flags'),
        key: 'flags'
      },
      {
        title: this.$t('Created_On'),
        key: 'date'
      },
      {
        title: this.$t('Created_By'),
        key: 'insertedBy'
      }
    ])

    return {
      showHistory: false,
      loading: false,
      mocVersionHistoryHeaders,
      approvals: [],
      showApprovalsDialog: false,
      showOwnerDecisionDialog: false,
      mocInHistory: {},
      selectedHistoryItem: {},
      selectedOwnerDecision: {},
      selectedOwnerDecisionStatus: {},
      showBeingRevisedReasonDialog: false,
      selectedRevisionReason: {},
      showSentBackReasonDialog: false,
      selectedSentBackReason: {},
      showCloseStrandedReasonDialog:false,
      selectedCloseStrandedReason:{}


    }
  },
  props: {
    moc: {
      type: Object
    }
  },
  components: {
    Toaster,
    ArchiveButton,
    Approvals,
    OwnerDecision,
    BeingRevisedReason,
    SentBackReason    
  },
  computed: {
    ...mapState('currentUserAdminRoles', ['adminRoles']),
    mocVersionHistoryItems() {
      if (!this.moc) return []
      if (!this.moc.history) return []
      const result = this.$_.orderBy(this.moc.history, item => moment(item.date).toDate(), ['desc'])
      return result.map((h, i) => ({ ...h, version: result.length - (i + 1) }))
    },
    canDownloadJson() {
      return (
        this.adminRoles &&
        this.adminRoles.filter(r => r.toLowerCase() === 'itsupport' || r.toLowerCase() === 'processmanager').length
      )
    },
    canSeeTasks() {
      return (
        this.$store.state.currentUserAdminRoles.adminRoles.filter(
          r => r.toLowerCase() === 'itsupport' || r.toLowerCase() === 'processmanager'
        ).length > 0
      )
    }
  },
  methods: {
    downloadVersionFile(item) {
      this.loading = true
      MOCService.getMocVersionFile(this.moc.communityData.communityAbbreviation, this.moc.mocId, item.versionId)
        .then(result => {
          downloadFile(JSON.stringify(result, null, 2), '.json', `${this.moc.mocId}-${item.version}-${item.status.name}.json`)
        })
        .finally(() => (this.loading = false))
    },
    downloadArchive() {
      this.loading = true
      MOCService.getArchive(this.moc.mocId)
        .then(result => {
          downloadFile(result, 'zip', `${this.moc.mocId}.zip`)
        })
        .finally(() => (this.loading = false))
    },
    downloadApprovalVersionFile(item) {
      this.loading = true
      ApprovalService.getApprovalVersionFile(this.moc.communityData.communityAbbreviation, this.moc.mocId, item.versionId)
        .then(result => {
          var data = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(result, null, 2))
          var downloadAnchorNode = document.createElement('a')
          downloadAnchorNode.setAttribute('href', data)
          downloadAnchorNode.setAttribute('download', `${this.moc.mocId}-Approvals.json`)
          document.body.appendChild(downloadAnchorNode)
          downloadAnchorNode.click()
          downloadAnchorNode.remove()
        })
        .finally(() => (this.loading = false))
    },
    downloadTasksVersionFile(item) {
      this.loading = true
      TaskService.getTaskVersionFile(this.moc.communityData.communityAbbreviation, this.moc.mocId, item.versionId)
        .then(result => {
          downloadFile(JSON.stringify(result, null, 2), '.json', `${this.moc.mocId}-Tasks.json`)
        })
        .finally(() => (this.loading = false))
    },
    getVersionColor(history) {
      const status = history.status
      if (history.action && history.action.toLowerCase() === 'owner rejected make permanent or extend change') {
        return 'error'
      }
      switch (status.value) {
        case FormStatus.SENTBACKFORCORRECTIONS:
        case FormStatus.DRAFT:
        case FormStatus.BEINGREVISED:
          return 'accent'
        case FormStatus.CLOSED:
        case FormStatus.CLOSEDREJECTED:
        case FormStatus.CLOSEDVOIDED:
        case FormStatus.CLOSEDSTRANDED:
          return 'error'
        default:
          return 'green'
      }
    },
    getFlags(item) {
      return (item.flags || []).map(f => Flags.properties[f].value)
    },
    getMocInHistory(index) {
      const history = this.mocVersionHistoryItems
      for (let i = 0; i < history.length; i++) {
        if (i < index) continue
        if (history[i] && history[i].versionId && !(history[i].approvals || {}).versionId) {
          return history[i]
        }
      }
    },
    getOwnerDecision: function(item) {
      return (item.ownerDecision || {}).decision || {}
    },
    setOwnerDecisionColor: function(val) {
      return val === DecisionTypes.properties[DecisionTypes.APPROVED].key ? 'success' : 'error'
    },
    showApprovals(item, index) {
      this.loading = true
      ApprovalService.getApprovalVersionFile(
        this.moc.communityData.communityAbbreviation,
        this.moc.mocId,
        item.approvals.versionId
      )
        .then(result => {
          this.approvals = this.objectKeysToCamelCase(result)
          const historyItemWithMocVersion = this.getMocInHistory(index)
          return MOCService.getMocVersionFile(
            this.moc.communityData.communityAbbreviation,
            this.moc.mocId,
            historyItemWithMocVersion.versionId
          )
        })
        .then(result => {
          this.mocInHistory = this.objectKeysToCamelCase(result)
          this.selectedHistoryItem = item
          this.showApprovalsDialog = true
        })
        .finally(() => (this.loading = false))
    },
    showOwnersDecision(item) {
      this.selectedOwnerDecision = item.ownerDecision
      this.showOwnerDecisionDialog = true
      this.selectedOwnerDecisionStatus = item.status
    },
    showBeingRevisedReason(item) {
      this.selectedRevisionReason = item.revisionReason
      this.showBeingRevisedReasonDialog = true
    },
    showSentBackReason(item) {
      this.selectedSentBackReason = item.sentBackReason
      this.showSentBackReasonDialog = true
    },
    showCloseStrandedReason(item){
      this.selectedCloseStrandedReason = item.closeStrandedReason
      this.showCloseStrandedReasonDialog =true

    },
    hideApprovals() {
      this.approvals = []
      this.showApprovalsDialog = false
      this.selectedHistoryItem = {}
      this.mocInHistory = {}
    },
    objectKeysToCamelCase(obj) {
      const _ = this.$_
      if (!_.isObject(obj)) {
        return obj
      } else if (_.isArray(obj)) {
        return obj.map(v => this.objectKeysToCamelCase(v))
      }
      return _.reduce(
        obj,
        (r, v, k) => {
          return {
            ...r,
            [_.camelCase(k)]: this.objectKeysToCamelCase(v)
          }
        },
        {}
      )
    }
  }
}
</script>
<style scoped>
.bold-text {
  font-weight: bold;
}

.approved {
  color: green;
}

.rejected {
  color: red;
}
.text-white{
  color:white !important;
}
</style>
